import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './TripConfigurationPanel.css';

// Debounce hook for autocomplete
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    
    return debouncedValue;
};

// Loading status messages
const LOADING_STAGES = [
    { message: 'Calculating route...', icon: 'route' },
    { message: 'Scanning for fuel stations...', icon: 'local_gas_station' },
    { message: 'Checking weather conditions...', icon: 'cloud' },
    { message: 'Analyzing traffic...', icon: 'traffic' },
    { message: 'Generating HOS logs...', icon: 'description' },
    { message: 'Almost there...', icon: 'hourglass_top' },
];

const TripConfigurationPanel = ({ setTripPlan, userProfile, darkMode, isCollapsed, onToggleCollapse }) => {
    const [formData, setFormData] = useState({
        startLocation: '',
        pickupLocation: '',
        dropoffLocation: '',
        cycleHours: '0'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStage, setLoadingStage] = useState(0);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [error, setError] = useState(null);
    const [gpsLoading, setGpsLoading] = useState(false);
    
    // Autocomplete state
    const [activeField, setActiveField] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestionsRef = useRef(null);
    const loadingIntervalRef = useRef(null);
    
    // Debounced search queries
    const debouncedStart = useDebounce(formData.startLocation, 300);
    const debouncedPickup = useDebounce(formData.pickupLocation, 300);
    const debouncedDropoff = useDebounce(formData.dropoffLocation, 300);

    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    // Loading animation effect - cycles through stages
    useEffect(() => {
        if (isLoading) {
            setLoadingStage(0);
            setElapsedTime(0);
            
            // Update elapsed time every 100ms
            const timeInterval = setInterval(() => {
                setElapsedTime(prev => prev + 0.1);
            }, 100);
            
            // Cycle through loading stages every 2 seconds
            const stageInterval = setInterval(() => {
                setLoadingStage(prev => (prev + 1) % LOADING_STAGES.length);
            }, 2000);
            
            loadingIntervalRef.current = { timeInterval, stageInterval };
            
            return () => {
                clearInterval(timeInterval);
                clearInterval(stageInterval);
            };
        } else {
            if (loadingIntervalRef.current) {
                clearInterval(loadingIntervalRef.current.timeInterval);
                clearInterval(loadingIntervalRef.current.stageInterval);
            }
            setLoadingStage(0);
            setElapsedTime(0);
        }
    }, [isLoading]);

    // Fetch autocomplete suggestions
    const fetchSuggestions = useCallback(async (query) => {
        if (!query || query.length < 3) {
            setSuggestions([]);
            return;
        }
        
        try {
            const response = await axios.get(`${apiBase}/api/geocode/`, {
                params: { action: 'search', q: query, limit: 5 }
            });
            setSuggestions(response.data || []);
            setShowSuggestions(true);
        } catch (err) {
            console.error('Autocomplete error:', err);
            setSuggestions([]);
        }
    }, [apiBase]);

    // Trigger autocomplete based on active field
    useEffect(() => {
        if (activeField === 'startLocation') fetchSuggestions(debouncedStart);
        else if (activeField === 'pickupLocation') fetchSuggestions(debouncedPickup);
        else if (activeField === 'dropoffLocation') fetchSuggestions(debouncedDropoff);
    }, [debouncedStart, debouncedPickup, debouncedDropoff, activeField, fetchSuggestions]);

    // Close suggestions on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setActiveField(name);
    };

    const handleFocus = (fieldName) => {
        setActiveField(fieldName);
        if (formData[fieldName]?.length >= 3) {
            setShowSuggestions(true);
        }
    };

    const handleSelectSuggestion = (suggestion) => {
        if (activeField) {
            setFormData({ ...formData, [activeField]: suggestion.short_name });
        }
        setShowSuggestions(false);
        setSuggestions([]);
    };

    // GPS: Get current location and reverse geocode
    const handleGpsClick = async () => {
        if (!navigator.geolocation) {
            setError("Geolocation is not supported by your browser.");
            return;
        }
        
        setGpsLoading(true);
        setError(null);
        
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                
                try {
                    // Reverse geocode the coordinates
                    const response = await axios.get(`${apiBase}/api/geocode/`, {
                        params: { action: 'reverse', lat: latitude, lon: longitude }
                    });
                    
                    if (response.data?.short_name) {
                        setFormData(prev => ({
                            ...prev,
                            startLocation: response.data.short_name
                        }));
                    } else {
                        // Fallback to coordinates
                        setFormData(prev => ({
                            ...prev,
                            startLocation: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
                        }));
                    }
                } catch (err) {
                    console.error('Reverse geocode error:', err);
                    // Fallback to coordinates
                    setFormData(prev => ({
                        ...prev,
                        startLocation: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
                    }));
                }
                
                setGpsLoading(false);
            },
            (err) => {
                console.error('Geolocation error:', err);
                setError("Could not get your location. Please enter it manually.");
                setGpsLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        // Basic validation
        if (!formData.startLocation || !formData.pickupLocation || !formData.dropoffLocation) {
            setError("All location fields are required.");
            return;
        }

        setIsLoading(true);
        setError(null);

        const cycleUsed = parseFloat(formData.cycleHours) || 0;

        try {
            const response = await axios.post(`${apiBase}/api/trip/`, {
                start_location_str: formData.startLocation,
                pickup_location_str: formData.pickupLocation,
                dropoff_location_str: formData.dropoffLocation,
                cycle_hours_used: cycleUsed,
            });
            
            const enhancedTrip = {
                ...response.data,
                meta: {
                    shippingDocs: formData.shippingDocs,
                    shipperCommodity: formData.shipperCommodity,
                    trailerNumber: formData.trailerNumber
                }
            };
            
            setTripPlan(enhancedTrip);
        } catch (err) {
            console.error("Error fetching trip plan:", err);
            setError("Failed to generate route. Check inputs or backend.");
        } finally {
            setIsLoading(false);
        }
    };

    // Calculate remaining hours
    const cycleUsed = parseFloat(formData.cycleHours) || 0;
    const driveRemaining = Math.max(0, 11 - (cycleUsed * 0.15)).toFixed(0);
    const windowRemaining = Math.max(0, 14 - (cycleUsed * 0.2)).toFixed(0);

    // Render autocomplete dropdown
    const renderSuggestions = (fieldName) => {
        if (!showSuggestions || activeField !== fieldName || suggestions.length === 0) {
            return null;
        }
        
        return (
            <ul className="autocomplete-suggestions" ref={suggestionsRef}>
                {suggestions.map((s, idx) => (
                    <li 
                        key={idx} 
                        onClick={() => handleSelectSuggestion(s)}
                        className="suggestion-item"
                    >
                        <span className="material-symbols-outlined">location_on</span>
                        <div className="suggestion-text">
                            <span className="suggestion-name">{s.short_name}</span>
                            <span className="suggestion-detail">{s.display_name}</span>
                        </div>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <aside className="trip-panel-v2">
            {/* Header */}
            <div className="panel-header">
                <h1>Plan Your Trip</h1>
                <p>Optimize your route & stay compliant.</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="panel-form">
                {/* Current Location */}
                <div className="form-group">
                    <label>
                        <span className="material-symbols-outlined">my_location</span>
                        Current Location
                    </label>
                    <div className="input-wrapper autocomplete-wrapper">
                        <input
                            type="text"
                            name="startLocation"
                            value={formData.startLocation}
                            onChange={handleChange}
                            onFocus={() => handleFocus('startLocation')}
                            placeholder="City, State"
                            autoComplete="off"
                            required
                        />
                        <button 
                            type="button" 
                            className={`gps-btn ${gpsLoading ? 'loading' : ''}`}
                            title="Use current location"
                            onClick={handleGpsClick}
                            disabled={gpsLoading}
                        >
                            <span className="material-symbols-outlined">
                                {gpsLoading ? 'sync' : 'gps_fixed'}
                            </span>
                        </button>
                        {renderSuggestions('startLocation')}
                    </div>
                </div>

                {/* Pickup Location */}
                <div className="form-group">
                    <label>
                        <span className="material-symbols-outlined">local_shipping</span>
                        Pickup Location
                    </label>
                    <div className="autocomplete-wrapper">
                        <input
                            type="text"
                            name="pickupLocation"
                            value={formData.pickupLocation}
                            onChange={handleChange}
                            onFocus={() => handleFocus('pickupLocation')}
                            placeholder="City, State"
                            autoComplete="off"
                            required
                        />
                        {renderSuggestions('pickupLocation')}
                    </div>
                </div>

                {/* Dropoff Location */}
                <div className="form-group">
                    <label>
                        <span className="material-symbols-outlined">location_on</span>
                        Dropoff Location
                    </label>
                    <div className="autocomplete-wrapper">
                        <input
                            type="text"
                            name="dropoffLocation"
                            value={formData.dropoffLocation}
                            onChange={handleChange}
                            onFocus={() => handleFocus('dropoffLocation')}
                            placeholder="City, State"
                            autoComplete="off"
                            required
                        />
                        {renderSuggestions('dropoffLocation')}
                    </div>
                </div>

                {/* Shipping Details for Log */}
                <div className="form-group">
                    <label>
                        <span className="material-symbols-outlined">receipt_long</span>
                        Shipping Documents
                    </label>
                    <input
                        type="text"
                        name="shippingDocs"
                        value={formData.shippingDocs || ''}
                        onChange={handleChange}
                        placeholder="BOL #, Manifest #"
                    />
                </div>
                
                <div className="form-group">
                    <label>
                        <span className="material-symbols-outlined">inventory_2</span>
                        Shipper & Commodity
                    </label>
                    <input
                        type="text"
                        name="shipperCommodity"
                        value={formData.shipperCommodity || ''}
                        onChange={handleChange}
                        placeholder="Acme Corp / Steel Coils"
                    />
                </div>

                <div className="form-group">
                    <label>
                        <span className="material-symbols-outlined">local_shipping</span>
                        Trailer Number
                    </label>
                    <input
                        type="text"
                        name="trailerNumber"
                        value={formData.trailerNumber || ''}
                        onChange={handleChange}
                        placeholder="Trailer #1234"
                    />
                </div>

                {/* Cycle Hours */}
                <div className="form-group">
                    <div className="label-row">
                        <label>
                            <span className="material-symbols-outlined">schedule</span>
                            Cycle Hours Used (7 Days)
                        </label>
                        <span className="max-label">Max 70h</span>
                    </div>
                    <div className="input-wrapper">
                        <input
                            type="number"
                            name="cycleHours"
                            value={formData.cycleHours}
                            onChange={handleChange}
                            min="0"
                            max="70"
                            step="0.5"
                        />
                        <span className="unit-label">Hours</span>
                    </div>
                </div>

                {/* Time Remaining Cards */}
                <div className="time-cards">
                    <div className="time-card">
                        <span className="time-label">11h Drive Remaining</span>
                        <span className="time-value">{driveRemaining}:00</span>
                    </div>
                    <div className="time-card">
                        <span className="time-label">14h Window</span>
                        <span className="time-value">{windowRemaining}:00</span>
                    </div>
                </div>

                {error && <p className="error-msg">{error}</p>}
            </form>

            {/* Submit Button */}
            <div className="panel-footer">
                {isLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner">
                            <div className="spinner-ring"></div>
                            <span className="material-symbols-outlined loading-icon">
                                {LOADING_STAGES[loadingStage].icon}
                            </span>
                        </div>
                        <div className="loading-info">
                            <span className="loading-message">{LOADING_STAGES[loadingStage].message}</span>
                            <span className="loading-time">{elapsedTime.toFixed(1)}s</span>
                        </div>
                        <div className="loading-progress">
                            <div 
                                className="loading-progress-bar" 
                                style={{ width: `${Math.min((loadingStage + 1) / LOADING_STAGES.length * 100, 95)}%` }}
                            />
                        </div>
                    </div>
                ) : (
                    <button 
                        type="submit" 
                        className="generate-btn"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        <span className="material-symbols-outlined">auto_awesome</span>
                        Generate Route & Logs
                    </button>
                )}
            </div>
        </aside>
    );
};

export default TripConfigurationPanel;
